import React, { createContext, useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/authService';
import sponsorService from '../services/sponsorService';
import permissionsService from '../services/permissionsService';
import mfaService from '../services/mfaService';
import { api } from '../services/api';
import { setReadOnlyMode } from '../services/devTools';
import { startShadowSession, endShadowSession } from '../services/shadowSession';
import toast from 'react-hot-toast';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  console.debug('[AUTH CONTEXT] Provider rendering');
  const mountCount = useRef(0);
  
  useEffect(() => {
    mountCount.current += 1;
    console.debug('[AUTH CONTEXT] Provider mounted, mount count:', mountCount.current);
    return () => console.debug('[AUTH CONTEXT] Provider unmounting');
  }, []);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState(null);
  const [user, setUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  // True once a permissions fetch has settled (success or failure) for the
  // current session. Lets route guards hold rendering until permissions are
  // known, so role-gated pages don't flash "Access Denied" on the empty
  // initial object (notably right after a QA "view as role" reload).
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [pendingAuth, setPendingAuth] = useState(null); // Store auth data while MFA is pending
  const [isLoginAttemptInProgress, setIsLoginAttemptInProgress] = useState(false); // Track login attempts
  // QA "view as" persona override (dev banner). Purely client-side: re-labels
  // the user's role/flags so role-gated UI renders as that persona would see it.
  // Always paired with read-only mode so no writes happen while impersonating.
  const [qaPersona, setQaPersonaState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('qa_persona')) || null;
    } catch {
      return null;
    }
  });
  const navigate = useNavigate();

  // Load user permissions when authenticated
  const loadUserPermissions = async () => {
    console.debug('[AUTH CONTEXT] Loading user permissions...');
    try {
      const permissions = await permissionsService.getUserPermissions();
      console.debug('[AUTH CONTEXT] User permissions loaded:', permissions);
      console.debug('[AUTH CONTEXT] Permissions object keys:', Object.keys(permissions));
      
      if (!permissions || Object.keys(permissions).length === 0) {
        console.warn('[AUTH CONTEXT] Received empty permissions object!');
      }
      
      setUserPermissions(permissions);
      setPermissionsLoaded(true);
      return permissions; // Return permissions for use in auth check
    } catch (error) {
      console.error('[AUTH CONTEXT] Error loading permissions:', error);
      console.error('[AUTH CONTEXT] Error details:', error.response?.data, error.message);
      // Set empty object on error to prevent undefined issues
      setUserPermissions({});
      setPermissionsLoaded(true);
      return {};
    }
  };

  // Establish sponsor context for an internal user, then route them (FECOA-1613).
  //
  // A single-sponsor internal user has no way to pick a sponsor by hand: the
  // sponsor selector is only offered when sponsor_count > 1, so nothing sets
  // `selectedSponsor` unless the auto-bypass on /api/sponsor/select/ runs. That
  // bypass used to be wired into the MFA paths only, which left non-MFA internal
  // users (Data Manager, for one) authenticated but with no sponsor, so every
  // sponsor-scoped screen — Assessment Builder included — came up empty.
  // All login paths funnel through here so the behavior is identical whether or
  // not MFA is in play.
  const ensureSingleSponsorContext = useCallback(async () => {
    if (localStorage.getItem('selectedSponsor')) {
      return;
    }

    try {
      // Auto-bypass: re-issues a sponsor-scoped token and sets selectedSponsor.
      const response = await sponsorService.getSponsors();
      if (response?.auto_selected) {
        // Tokens and selectedSponsor are already persisted by sponsorService.
        window.dispatchEvent(new Event('storage'));
      } else {
        console.warn('[AUTH CONTEXT] Expected sponsor auto-bypass but got a sponsor list');
      }
    } catch (error) {
      console.error('[AUTH CONTEXT] Error in sponsor auto-bypass:', error);
    }
  }, []);

  const resolveSponsorContextAndNavigate = useCallback(async (sponsorCount) => {
    if (sponsorCount > 1) {
      navigate('/sponsor-selector');
      return;
    }

    if (sponsorCount === 1) {
      await ensureSingleSponsorContext();
    }

    navigate('/studies');
  }, [navigate, ensureSingleSponsorContext]);

  // Effect to load permissions whenever authentication state changes
  // Use a ref to track if permissions are already loading to prevent multiple calls
  const permissionsLoadingRef = useRef(false);
  
  useEffect(() => {
    if (isAuthenticated && !permissionsLoadingRef.current) {
      permissionsLoadingRef.current = true;
      loadUserPermissions().finally(() => {
        permissionsLoadingRef.current = false;
      });
    } else if (!isAuthenticated) {
      setUserPermissions({});
      setPermissionsLoaded(false);
      permissionsLoadingRef.current = false;
    }
  }, [isAuthenticated]);

  const checkAuthStatus = useCallback(async () => {
    console.debug('[AUTH CONTEXT] Checking auth status...');
    
    // CRITICAL FIX: Don't interfere with active login attempts
    if (isLoginAttemptInProgress) {
      console.debug('[AUTH CONTEXT] Login attempt in progress, skipping auth check to prevent interference');
      return;
    }
    
    // We still perform auth check even on login page to enable redirects
    const isLoginPage = window.location.pathname === '/login';
    if (isLoginPage) {
      console.debug('[AUTH CONTEXT] On login page, still checking auth for redirect purposes');
    }

    try {
      const authToken = localStorage.getItem('authToken');
      console.debug('[AUTH CONTEXT] Auth token exists:', !!authToken);
      
      if (!authToken) {
        console.debug('[AUTH CONTEXT] No auth token, clearing state');
        // CRITICAL FIX: Only clear state if not on login page to prevent interference
        if (!isLoginPage) {
          setIsAuthenticated(false);
          setUser(null);
          setUserType(null);
        }
        setLoading(false);
        return;
      }

      const status = await AuthService.checkAuthStatus();
      console.debug('[AUTH CONTEXT] Auth status response:', status);
      
      if (status.isAuthenticated && status.user) {
        console.debug('[AUTH CONTEXT] User is authenticated, checking MFA status');
        
        // Set user data first
        setUser(status.user);
        if (status.user_type) {
          console.debug('[AUTH CONTEXT] Setting user type:', status.user_type);
          setUserType(status.user_type);
        }
                // Handle MFA state from auth status (only present if MFA is required)
        if (status.mfaSetupRequired !== undefined || status.mfaRequired !== undefined) {
          console.debug('[AUTH CONTEXT] MFA flags from auth status:', {
            mfaSetupRequired: status.mfaSetupRequired,
            mfaRequired: status.mfaRequired,
            mfa_method: status.mfa_method
          });
          
          // CRITICAL SECURITY FIX: If MFA is required but not completed, DO NOT set isAuthenticated = true
          if (status.mfaSetupRequired || status.mfaRequired) {
            console.debug('[AUTH CONTEXT] 🚨 MFA REQUIRED - User not fully authenticated until MFA complete');
            console.debug('[AUTH CONTEXT] MFA method from auth status:', status.mfa_method);
            
            // Store pending auth data with MFA method
            setPendingAuth({
              user: status.user,
              user_type: status.user_type,
              mfa_method: status.mfa_method || 'totp',  // Capture MFA method from auth status
              tokens: { 
                access_token: authToken,
                refresh_token: localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken')
              }
            });
            
            setMfaSetupRequired(status.mfaSetupRequired || false);
            setMfaRequired(status.mfaRequired || false);
            
            // SECURITY: Do NOT set isAuthenticated = true until MFA complete
            setIsAuthenticated(false);
            
            setLoading(false);
            return; // Exit early - user is NOT fully authenticated
          }
          
          // If we reach here, MFA was required but is now complete
          setMfaSetupRequired(false);
          setMfaRequired(false);
          setPendingAuth(null);
        } else {
          console.debug('[AUTH CONTEXT] No MFA flags in auth status - MFA not required for this user');
          setMfaSetupRequired(false);
          setMfaRequired(false);
          setPendingAuth(null);
        }
        
        // Only set fully authenticated if no pending MFA
        console.debug('[AUTH CONTEXT] Setting fully authenticated state - no pending MFA');
        setIsAuthenticated(true);
        
        // Load user permissions first to check sponsor count
        const permissions = await loadUserPermissions();
        const sponsorCount = permissions?.sponsor_count || 0;
        
        // Sync localStorage sponsor with JWT token sponsor_id ONLY for single-sponsor users
        if (status.user.is_internal && status.user.sponsor_id && sponsorCount === 1) {
          const existingSponsor = localStorage.getItem('selectedSponsor');
          if (!existingSponsor) {
            console.debug('[AUTH CONTEXT] Setting sponsor from JWT token for single-sponsor user:', status.user.sponsor_id);
            localStorage.setItem('selectedSponsor', JSON.stringify({
              id: status.user.sponsor_id,
              name: status.user.sponsor_name || status.user.sponsor_id
            }));
          }
        } else if (status.user.is_internal && !status.user.sponsor_id && sponsorCount === 1) {
          // Single sponsor but the token carries no sponsor_id, so there is nothing
          // to copy across. Fall back to the same auto-bypass login uses, otherwise
          // a refresh leaves the user authenticated with no sponsor (FECOA-1613).
          console.debug('[AUTH CONTEXT] Single-sponsor user with no token sponsor_id - resolving via auto-bypass');
          await ensureSingleSponsorContext();
        } else if (status.user.is_internal && !status.user.sponsor_id && sponsorCount === 0) {
          // Only clear if user truly has no sponsors
          console.debug('[AUTH CONTEXT] Clearing stale sponsor data - user has no sponsors');
          localStorage.removeItem('selectedSponsor');
        }
      } else {
        console.debug('[AUTH CONTEXT] Setting unauthenticated state');
        setIsAuthenticated(false);
        setUser(null);
        setUserType(null);
        setUserPermissions({});
        localStorage.removeItem('selectedSponsor');
      }
    } catch (error) {
      console.error('[AUTH CONTEXT] Auth check error:', error);
      setIsAuthenticated(false);
      setUser(null);
      setUserType(null);
      setUserPermissions({});
      localStorage.removeItem('selectedSponsor');
    } finally {
      console.debug('[AUTH CONTEXT] Auth check complete');
      setLoading(false);
    }
  }, [isLoginAttemptInProgress, ensureSingleSponsorContext]);

  useEffect(() => {
    console.debug('[AUTH CONTEXT] Initial auth check triggered');
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async (email, password, rememberMe = false) => {
    console.debug('[AUTH CONTEXT] Login attempt started for:', email);
    // Defensively clear any stale QA "view as role" state before the login POST.
    // A lingering qa_readonly flag makes the axios interceptor reject the POST
    // (no response → "Unable to connect to the server"), which would otherwise
    // leave a user stranded on the login page if shadow state survived a prior
    // session. Runs synchronously so it takes effect before AuthService.login.
    endShadowSession();
    localStorage.removeItem('qa_persona');
    setReadOnlyMode(false);
    setQaPersonaState(null);
    // CRITICAL FIX: Prevent ALL AuthContext state changes until we know the outcome
    // Let Login component handle loading/error state to prevent Provider re-renders
    setIsLoginAttemptInProgress(true); // Track login attempt to prevent auth check interference
    
    try {
      console.debug('[AUTH CONTEXT] Calling AuthService.login...');
      const response = await AuthService.login(email, password, rememberMe);
      console.debug('[AUTH CONTEXT] Login response received:', {
        hasTokens: !!response.tokens,
        hasUser: !!response.user,
        mfaSetupRequired: response.mfa_setup_required,
        mfaRequired: response.mfa_required,
        responseKeys: Object.keys(response)
      });
      
      // Handle MFA requirements first
      if (response.mfa_setup_required) {
        console.debug('[AUTH CONTEXT] 🚨 SECURITY: MFA setup required - NOT setting authenticated until MFA complete');
        // SECURITY FIX: Do NOT set isAuthenticated=true until MFA is complete
        setIsAuthenticated(false);
        setUserType(response.user_type);
        setUser(response.user);
        
        // Set MFA state
        setPendingAuth(response);
        setMfaSetupRequired(true);
        setMfaRequired(false);
        
        // CRITICAL: Set loading to false so the UI can render
        setLoading(false);
        
        // Don't navigate - stay on login page to show MFA setup
        return response;
      }
      
      if (response.mfa_required) {
        console.debug('[AUTH CONTEXT] 🚨 SECURITY: MFA verification required - NOT setting authenticated until MFA complete');
        console.debug('[AUTH CONTEXT] MFA method from backend:', response.mfa_method);
        // SECURITY FIX: Do NOT set isAuthenticated=true until MFA is complete
        setIsAuthenticated(false);
        setUserType(response.user_type);
        setUser(response.user);
        
        // Set MFA state with method from backend
        setPendingAuth({
          ...response,
          mfa_method: response.mfa_method || 'totp'  // Use backend method or default to totp
        });
        setMfaRequired(true);
        setMfaSetupRequired(false);
        
        // CRITICAL: Set loading to false so the UI can render
        setLoading(false);
        
        // Don't navigate - stay on login page to show MFA verification
        return response;
      }
      
      // Normal login flow - complete authentication
      if (response?.user) {
        console.log('[AUTH CONTEXT] Setting authenticated state with user:', {
          email: response.user.email,
          role: response.user.role,
          requiresPasswordChange: response.user.requires_password_change
        });
        setIsAuthenticated(true);
        setUserType(response.user_type);
        setUser(response.user);
        
        // Clear MFA states
        setMfaRequired(false);
        setMfaSetupRequired(false);
        setPendingAuth(null);
        
        // Load user permissions after successful login
        console.log('[AUTH CONTEXT] Loading user permissions after login');
        loadUserPermissions();

        // Check if password change is required
        if (response.user.requires_password_change) {
          console.log('[AUTH CONTEXT] Password change required, redirecting to password reset');
          navigate('/change-password');
          return response;
        }

        // Handle navigation based on user type
        if (response.user_type === 'participant') {
          console.log('[AUTH CONTEXT] Navigating to participant dashboard');
          navigate('/participant-dashboard');
        } else if (response.user.is_superuser) {
          // Superusers should always be able to reach the sponsor selector, even with a single sponsor
          console.log('[AUTH CONTEXT] Superuser login - navigating to sponsor selector');
          navigate('/sponsor-selector');
        } else if (response.user.is_internal) {
          // For internal users, check if they need sponsor selection
          // Load permissions first to get sponsor count
          try {
            const permissions = await permissionsService.getUserPermissions();
            const sponsorCount = permissions.sponsor_count || 0;

            console.log('[AUTH CONTEXT] Internal user sponsor count:', sponsorCount);

            await resolveSponsorContextAndNavigate(sponsorCount);
          } catch (error) {
            console.error('[AUTH CONTEXT] Error getting sponsor count, defaulting to studies:', error);
            navigate('/studies');
          }
        } else {
          console.log('[AUTH CONTEXT] Navigating to studies');
          navigate('/studies');
        }
      }

      return response;
    } catch (error) {
      console.error('[AUTH CONTEXT] Login error:', {
        status: error.response?.status,
        message: error.message
      });
      
      // CRITICAL: Always clear loading state on error
      setLoading(false);
      
      // Don't modify other auth state on failures, just let Login component handle the error
      if (error.response?.status === 401) {
        console.log('[AUTH CONTEXT] Authentication failed - login form can now show error');
      }
      
      throw error;
    } finally {
      // CRITICAL FIX: Allow state changes for successful MFA flows, prevent only for failures
      // Clear loading state for successful flows (MFA) but not for failed attempts (401)
      if (!error || (error && error.response?.status !== 401 && error.response?.status !== 429)) {
        setLoading(false); // Allow for successful flows that need MFA state management
      }
      setIsLoginAttemptInProgress(false); // Always clear tracking
    }  
  };

  const logout = async () => {
    console.log('[AUTH CONTEXT] Logout started');
    // Store the current user type before clearing state
    const currentUserType = userType;
    
    // Recover the study code so logout can redirect to /#<studyCode>.
    // Prefer the value stashed at login (source of truth from the URL hash);
    // fall back to the URL path if we're clearly on a participant route.
    // The email domain ('<study>-<sponsor>.flexcoa.com') can't be reliably
    // split post-hoc since both segments may contain hyphens.
    const isParticipantContext =
      currentUserType === 'participant' ||
      window.location.pathname.includes('/participant');
    const studyCode = localStorage.getItem('participantStudyCode') || '';
    
    try {
      await AuthService.logout();
      console.log('[AUTH CONTEXT] Logout API call successful');
    } catch (error) {
      console.error('[AUTH CONTEXT] Logout API call error:', error);
      // We still continue with client-side cleanup even if API call fails
    }

    // Clear auth token and all user data regardless of API call result
    console.log('[AUTH CONTEXT] Clearing auth state');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('refreshToken');
    localStorage.removeItem('selectedSponsor');
    // Clear after we've already captured studyCode above for the redirect.
    localStorage.removeItem('participantStudyCode');

    // Tear down any QA "view as role" shadow session. If left behind, the
    // qa_readonly flag keeps the axios request interceptor blocking every
    // write — including the next login POST — which surfaces to the user as
    // "Unable to connect to the server."
    endShadowSession();
    localStorage.removeItem('qa_persona');
    setReadOnlyMode(false);
    setQaPersonaState(null);

    // Reset auth context state
    setIsAuthenticated(false);
    setUser(null);
    setUserType(null);
    setUserPermissions({});
    
    // Clear login page redirect flag
    const isLoginPage = window.location.pathname === '/login';
    
    if (!isLoginPage) {
      const isParticipantPage = window.location.pathname.includes('/participant');
      if (isParticipantPage || currentUserType === 'participant') {
        // Send participants back to their study's login portal so they can sign
        // in again with their PIN, instead of dumping them on the marketing
        // home page. The portal lives at /#<studyCode>.
        const target = studyCode ? `/#${studyCode}` : '/';
        console.log(`[AUTH CONTEXT] Redirecting participant to ${target}`);
        navigate(target);
        localStorage.removeItem('participantData');
      } else {
        console.log('[AUTH CONTEXT] Redirecting to home page');
        navigate('/');
      }
    }
  };

  const refreshToken = async () => {
    console.log('[AUTH CONTEXT] Refresh token attempt started');
    try {
      await AuthService.refreshToken();
      console.log('[AUTH CONTEXT] Refresh token successful');
      setIsAuthenticated(true);
    } catch (error) {
      console.error('[AUTH CONTEXT] Refresh token error:', error);
      logout();
    }
  };

  const handleSponsorSelect = async (sponsorId) => {
    console.log('[AUTH CONTEXT] Sponsor select attempt started');
    try {
      const response = await sponsorService.selectSponsor(sponsorId);
      console.log('[AUTH CONTEXT] Sponsor select response received:', response);
      
      // Update auth token and user state with new sponsor info
      if (response.tokens?.access_token) {
        console.log('[AUTH CONTEXT] Updating auth token with sponsor info');
        // Update auth token which now includes sponsor_id
        localStorage.setItem('authToken', response.tokens.access_token);
        api.defaults.headers.common['Authorization'] = `Bearer ${response.tokens.access_token}`;
        
        // Store refresh token if provided
        if (response.tokens.refresh_token) {
          console.log('[AUTH CONTEXT] Storing refresh token');
          if (localStorage.getItem('refreshToken')) {
            localStorage.setItem('refreshToken', response.tokens.refresh_token);
          } else {
            sessionStorage.setItem('refreshToken', response.tokens.refresh_token);
          }
        }
        
        // Update user state with selected sponsor
        console.log('[AUTH CONTEXT] Updating user state with sponsor info');
        setUser(prevUser => ({
          ...prevUser,
          sponsor: {
            id: response.sponsor_id,
            name: response.sponsor_name
          }
        }));
        
        // Redirect to dashboard after successful sponsor selection
        console.log('[AUTH CONTEXT] Navigating to dashboard');
        navigate('/dashboard');
      } else {
        console.error('[AUTH CONTEXT] No access token in sponsor selection response:', response);
        toast.error('Failed to select sponsor. Please try again.');
      }
    } catch (error) {
      console.error('[AUTH CONTEXT] Sponsor select error:', error);
      toast.error('Failed to select sponsor. Please try again.');
    }
  };

  // Function to update context values directly
  const updateAuthContext = (updates) => {
    console.log('[AUTH CONTEXT] Updating context with:', updates);
    
    if (updates.user) {
      setUser(prev => ({ ...prev, ...updates.user }));
    }
    
    if (updates.userType) {
      setUserType(updates.userType);
    }
    
    if (updates.isAuthenticated !== undefined) {
      setIsAuthenticated(updates.isAuthenticated);
    }
  };

  // Handle MFA setup completion
  const completeMFASetup = async () => {
    console.log('[AUTH CONTEXT] MFA setup completed, completing authentication');
    if (pendingAuth?.user) {
      setIsAuthenticated(true);
      setUserType(pendingAuth.user_type);
      setUser(pendingAuth.user);
      
      // Clear MFA states
      setMfaRequired(false);
      setMfaSetupRequired(false);
      setPendingAuth(null);
      
      // Load user permissions
      loadUserPermissions();
      
      // Handle navigation based on user type
      if (pendingAuth.user_type === 'participant') {
        navigate('/participant-dashboard');
      } else if (pendingAuth.user.is_superuser) {
        console.log('[AUTH CONTEXT] Superuser MFA setup complete - navigating to sponsor selector');
        navigate('/sponsor-selector');
      } else if (pendingAuth.user.is_internal) {
        // For internal users, check sponsor count
        try {
          const permissions = await permissionsService.getUserPermissions();
          const sponsorCount = permissions.sponsor_count || 0;
          
          await resolveSponsorContextAndNavigate(sponsorCount);
        } catch (error) {
          console.error('[AUTH CONTEXT] Error getting sponsor count in MFA setup, defaulting to studies:', error);
          navigate('/studies');
        }
      } else {
        navigate('/studies');
      }
    }
  };
  
  // Handle MFA verification completion
  const completeMFAVerification = async (verificationResult) => {
    console.log('[AUTH CONTEXT] MFA verification completed, completing authentication');
    console.log('[AUTH CONTEXT] Verification result:', verificationResult);
    
    // CRITICAL FIX: Update tokens if provided in verification result
    // Backend now returns tokens directly (access_token, refresh_token) not nested under .tokens
    if (verificationResult.access_token) {
      console.log('[AUTH CONTEXT] Updating tokens with MFA completion flag');
      localStorage.setItem('authToken', verificationResult.access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${verificationResult.access_token}`;
      
      if (verificationResult.refresh_token) {
        if (localStorage.getItem('refreshToken')) {
          localStorage.setItem('refreshToken', verificationResult.refresh_token);
        } else {
          sessionStorage.setItem('refreshToken', verificationResult.refresh_token);
        }
      }
    } else {
      console.log('[AUTH CONTEXT] No new tokens provided in verification result - using existing tokens');
    }
    
    if (pendingAuth?.user) {
      setIsAuthenticated(true);
      setUserType(pendingAuth.user_type);
      setUser(pendingAuth.user);
      
      // Clear MFA states
      setMfaRequired(false);
      setMfaSetupRequired(false);
      setPendingAuth(null);
      
      // Load user permissions
      loadUserPermissions();
      
      // Handle navigation based on user type
      if (pendingAuth.user_type === 'participant') {
        navigate('/participant-dashboard');
      } else if (pendingAuth.user.is_internal) {
        // For internal users, check sponsor count
        try {
          const permissions = await permissionsService.getUserPermissions();
          const sponsorCount = permissions.sponsor_count || 0;
          
          await resolveSponsorContextAndNavigate(sponsorCount);
        } catch (error) {
          console.error('[AUTH CONTEXT] Error getting sponsor count in MFA verification, defaulting to studies:', error);
          navigate('/studies');
        }
      } else {
        navigate('/studies');
      }
    }
  };
  
  // Cancel MFA flow
  const cancelMFA = () => {
    console.log('[AUTH CONTEXT] MFA flow cancelled');
    setMfaRequired(false);
    setMfaSetupRequired(false);
    setPendingAuth(null);
  };

  // Force clear MFA state and logout - for debugging stuck states
  const clearMFAState = () => {
    console.log('[AUTH CONTEXT] Force clearing MFA state and logging out');
    setMfaRequired(false);
    setMfaSetupRequired(false);
    setPendingAuth(null);
    logout();
  };

  // Wrapper for participant login to handle state updates and redirection
  const participantLogin = async (participantNumber, pin, studyCode, rememberMe = false) => {
    console.log('[AUTH CONTEXT] Participant login attempt started');
    try {
      const response = await AuthService.participantLogin(participantNumber, pin, studyCode, rememberMe);
      console.log('[AUTH CONTEXT] Participant login response received:', {
        userType: response.user_type,
        user: response.user ? 'present' : 'missing'
      });

      if (response?.user) {
        console.log('[AUTH CONTEXT] Setting authenticated state for participant');
        setIsAuthenticated(true);
        setUserType('participant');
        setUser(response.user);

        // Stash the study code so logout can redirect back to the right
        // /#<studyCode> portal. The email domain is <study>-<sponsor>.flexcoa.com
        // (see bulk_participant_actions.py:205) and there's no reliable way
        // to split it post-hoc, so we save the source-of-truth value the
        // participant actually signed in with.
        if (studyCode) {
          localStorage.setItem('participantStudyCode', studyCode);
        }

        // Redirect to participant dashboard
        console.log('[AUTH CONTEXT] Navigating to participant dashboard');
        navigate('/participant-dashboard');
      }

      return response;
    } catch (error) {
      console.error('[AUTH CONTEXT] Participant login error:', {
        status: error.response?.status,
        message: error.message
      });
      throw error;
    }
  };

  // Accepts an already-received login response (from a prior API call — e.g. first-login check) and
  // finalizes the participant session without triggering a second /api/participant/login/ round trip.
  const finalizeParticipantSession = (loginData, rememberMe = false) => {
    const response = AuthService.storeParticipantSession(loginData, rememberMe);
    if (response?.user) {
      setIsAuthenticated(true);
      setUserType('participant');
      setUser(response.user);
      // Stash the study code from the URL hash so logout can redirect back
      // to the right /#<studyCode> portal (see participantLogin above).
      const studyCodeFromHash = (window.location.hash || '').replace(/^#/, '');
      if (studyCodeFromHash) {
        localStorage.setItem('participantStudyCode', studyCodeFromHash);
      }
      navigate('/participant-dashboard');
    }
    return response;
  };

  // Apply / clear a QA persona. Mints a real backend shadow token scoped to the
  // persona's role + study/site, swaps it in, and reloads permissions so the UI
  // reflects what that role actually sees (backend-enforced). Read-only mode is
  // enabled so nothing can be mutated. Pass null to exit and restore the real
  // session. Returns a promise; throws if the backend rejects the persona.
  const setQaPersona = useCallback(async (persona) => {
    if (persona) {
      // If already impersonating, restore the real token + lift read-only first
      // so the mint call authenticates as the real user and isn't self-blocked.
      endShadowSession();
      setReadOnlyMode(false);
      await startShadowSession({
        role: persona.role,
        studyId: persona.scope?.studyId || null,
        siteId: persona.scope?.siteId || null,
      });
      localStorage.setItem('qa_persona', JSON.stringify(persona));
      setReadOnlyMode(true);
      setQaPersonaState(persona);
    } else {
      endShadowSession();
      localStorage.removeItem('qa_persona');
      setReadOnlyMode(false);
      setQaPersonaState(null);
    }
    // Soft refresh so all cached data (React Query, Redux, in-flight lists) is
    // refetched against the now-active token. Without this, the previous
    // session's data lingers — e.g. an empty study list survives the swap.
    window.location.reload();
  }, []);

  // The user object handed to consumers. When a QA persona is active, its
  // role/flags are overlaid so role-gated routes and menus reflect that persona.
  // `realUser` (below) always exposes the true identity for the banner.
  const effectiveUser = useMemo(() => {
    if (!user || !qaPersona) return user;
    return {
      ...user,
      role: qaPersona.role ?? user.role,
      is_internal: qaPersona.is_internal ?? user.is_internal,
      is_superuser: qaPersona.is_superuser ?? user.is_superuser,
      _qaImpersonated: true,
    };
  }, [user, qaPersona]);

  const value = useMemo(() => ({
    isAuthenticated,
    loading,
    error,
    userType,
    user: effectiveUser,
    realUser: user,
    qaPersona,
    setQaPersona,
    userPermissions,
    permissionsLoaded,
    mfaRequired,
    mfaSetupRequired,
    pendingAuth,
    login,
    logout,
    checkAuthStatus,
    refreshToken,
    handleSponsorSelect,
    updateAuthContext,
    participantLogin,
    finalizeParticipantSession,
    loadUserPermissions,
    completeMFASetup,
    completeMFAVerification,
    cancelMFA,
    clearMFAState,
  }), [
    isAuthenticated,
    loading,
    error,
    userType,
    user,
    effectiveUser,
    qaPersona,
    setQaPersona,
    userPermissions,
    permissionsLoaded,
    mfaRequired,
    mfaSetupRequired,
    pendingAuth,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};